class Code27PanelX extends HTMLElement {
	constructor() {
		super();
		this._currentExpandedIndex = -1;
		this._hasUserInteracted = false;
		this._panels = [];
		this._data = []; // 默认空数组
		this._currentSlide = 0;
		this._isMobile = false;
	}

	static get observedAttributes() {
		return ['data', 'heading'];
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (name === 'data' && newValue !== oldValue) {
			try {
				// 移除多余的空白字符
				const cleanedValue = newValue.replace(/\s+/g, ' ').trim();
				this._data = JSON.parse(decodeURIComponent(cleanedValue));
				this._render();
			} catch (e) {
				console.error('Invalid data format:', e);
				console.log('Attempted to parse:', newValue);
			}
		} else if (name === 'heading' && newValue !== oldValue) {
			this._heading = newValue;
			this._render();
		}
	}

	connectedCallback() {
		this._heading =
			this.getAttribute('heading') || 'Life with Your Codie, 24/7';
		this._checkMobile();
		this._render();
		this._bindEvents();
		this._handleResize();
		if (!this._isMobile) {
			this._setDefaultExpanded();
		}
	}

	_checkMobile() {
		this._isMobile = window.innerWidth < 1024; // lg断点
	}

	_handleResize() {
		window.addEventListener('resize', () => {
			const wasMobile = this._isMobile;
			this._checkMobile();
			if (wasMobile !== this._isMobile) {
				this._render(); // 重新渲染当布局需要改变时
				this._bindEvents();
				if (!this._isMobile) {
					this._setDefaultExpanded();
				}
			}
		});
	}

	_render() {
		if (!Array.isArray(this._data) || this._data.length === 0) {
			console.warn('No valid panel data available');
			return;
		}
		this.innerHTML = this._template();
		this._panels = Array.from(this.querySelectorAll('.panel'));
		this._addStyles();

		// 移动端不需要额外初始化，箭头已经静态生成
	}

	_template() {
		if (this._isMobile) {
			return this._mobileTemplate();
		} else {
			return this._desktopTemplate();
		}
	}

	_mobileTemplate() {
		return `
			<div class="max-w-10xl mx-auto px-4 py-8 relative">
				<h1 class="text-6xl font-semibold text-center mb-8 text-white">${
					this._heading
				}</h1>

				<!-- 移动端水平滚动容器 -->
				<div class="mobile-panel-container relative">
					<div class="mobile-panel-slider flex overflow-x-scroll snap-x snap-mandatory scrollbar-hide" id="mobilePanelSlider">
						${this._data
							.map((data, index) => this._mobilePanelTemplate(data, index))
							.join('')}
					</div>

					<!-- 分页导航点 -->
					<div class="pagination-dots flex justify-center mt-6 space-x-3">
						${this._data
							.map(
								(_, index) => `
							<button class="pagination-dot w-3 h-3 rounded-full transition-all duration-300 ${
								index === 0 ? 'active' : 'bg-gray-600'
							}" data-slide="${index}"></button>
						`
							)
							.join('')}
					</div>
				</div>
			</div>
		`;
	}

	_desktopTemplate() {
		return `
			<div class="max-w-[1200px] mx-auto px-4 py-8 relative">
				<h1 class="text-6xl font-semibold text-center mb-8 text-white">${
					this._heading
				}</h1>
				<div class="flex min-h-[440px] rounded-2xl overflow-hidden">
					${this._data.map((data) => this._panelTemplate(data)).join('')}
				</div>
			</div>
		`;
	}

	_mobilePanelTemplate(data, index) {
		const {title = '', description = '', image = ''} = data;
		const showPrevArrow = index > 0;
		const showNextArrow = index < this._data.length - 1;

		return `
			<div class="mobile-slide flex-none w-[96%] snap-center px-2" data-slide="${index}">
				<div class="panel relative rounded-2xl overflow-hidden aspect-[8/5] w-full bg-cover bg-center"
					 style="background-image: url(${image})">

					<!-- 每个slide独立的箭头 -->
					${
						showPrevArrow
							? `
					<button class="slide-nav-arrow slide-prev-arrow" data-slide="${index}" data-direction="prev">
						<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
						</svg>
					</button>
					`
							: ''
					}

					${
						showNextArrow
							? `
					<button class="slide-nav-arrow slide-next-arrow" data-slide="${index}" data-direction="next">
						<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
						</svg>
					</button>
					`
							: ''
					}

					<div class="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent text-white">
						<h3 class="text-3xl font-semibold mb-3 text-white">${title}</h3>
						<p class="text-xl leading-relaxed text-white">${description}</p>
					</div>
				</div>
			</div>
		`;
	}

	_panelTemplate(data) {
		const {title = '', description = '', image = ''} = data;
		return `
			<div class="panel flex-1 relative cursor-pointer bg-cover bg-center transition-all duration-700 ease-in-out overflow-hidden"
				 style="background-image: url(${image})">
				<div class="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/70 to-transparent text-white">
					<h3 class="text-4xl font-semibold mb-2 transition-opacity duration-700 ease-in-out text-white">${title}</h3>
					<p class="opacity-0 transition-opacity duration-700 ease-in-out line-clamp-2">${description}</p>
				</div>
			</div>
		`;
	}

	_addStyles() {
		// 移除旧样式标签如果存在
		const existingStyle = this.querySelector('style');
		if (existingStyle) {
			existingStyle.remove();
		}

		const styleEl = document.createElement('style');
		styleEl.textContent = `
			/* 移动端样式 */
			.scrollbar-hide {
				-ms-overflow-style: none;
				scrollbar-width: none;
			}

			.scrollbar-hide::-webkit-scrollbar {
				display: none;
			}

			.mobile-panel-slider {
				scroll-behavior: smooth;
			}

			.mobile-slide {
				scroll-snap-align: center;
			}

			.pagination-dot {
				cursor: pointer;
				transition: all 0.3s ease;
				transform-origin: center;
			}

			.pagination-dot:hover {
				transform: scale(1.2);
			}

			.pagination-dot.active {
				background: linear-gradient(45deg, #6366f1, #8b5cf6);
				transform: scale(1.3);
				box-shadow: 0 0 12px rgba(139, 92, 246, 0.7);
			}

			.slide-nav-arrow {
				position: absolute;
				top: 50%;
				transform: translateY(-50%);
				width: 3rem;
				height: 3rem;
				background-color: rgba(0, 0, 0, 0.5);
				border-radius: 50%;
				display: flex;
				align-items: center;
				justify-content: center;
				transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
				cursor: pointer;
				backdrop-filter: blur(4px);
				border: 1px solid rgba(255, 255, 255, 0.2);
				-webkit-tap-highlight-color: transparent;
				z-index: 10;
			}

			.slide-prev-arrow {
				left: 1rem;
			}

			.slide-next-arrow {
				right: 1rem;
			}

			.slide-nav-arrow:hover {
				background-color: rgba(0, 0, 0, 0.7);
				transform: translateY(-50%) scale(1.1);
				box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
			}

			.slide-nav-arrow:active {
				transform: translateY(-50%) scale(0.95);
			}

			.slide-nav-arrow svg {
				transition: transform 0.2s ease;
			}

			.slide-nav-arrow:hover svg {
				transform: scale(1.1);
			}

			/* 确保移动端布局正确 */
			@media (max-width: 1023px) {
				.mobile-panel-container {
					position: relative;
				}

				.mobile-panel-slider {
					display: flex;
					overflow-x: scroll;
					scroll-snap-type: x mandatory;
					-webkit-overflow-scrolling: touch;
				}

				.mobile-slide {
					flex: 0 0 96%;
					width: 96%;
					scroll-snap-align: start;
				}

				.mobile-panel-slider {
					padding-left: 0.5rem;
					padding-right: 0.5rem;
				}
			}

			/* 桌面端保持原有样式 */
			@media (min-width: 1024px) {
				.panel {
					transition: all 0.7s ease-in-out;
				}
			}
		`;

		this.appendChild(styleEl);
	}

	_bindEvents() {
		if (this._isMobile) {
			this._bindMobileEvents();
		} else {
			this._bindDesktopEvents();
		}
	}

	_bindMobileEvents() {
		const slider = this.querySelector('#mobilePanelSlider');
		const dots = this.querySelectorAll('.pagination-dot');

		if (!slider || !dots.length) return;

		// 处理滑动事件
		let isScrolling = false;
		slider.addEventListener('scroll', () => {
			if (isScrolling) return;

			isScrolling = true;
			requestAnimationFrame(() => {
				const scrollLeft = slider.scrollLeft;
				const containerWidth = slider.offsetWidth;
				const slideWidth = containerWidth * 0.96; // 96% 宽度 + gap
				const newSlide = Math.round(scrollLeft / slideWidth);

				if (newSlide !== this._currentSlide) {
					this._currentSlide = newSlide;
					this._updatePaginationDots();
				}

				isScrolling = false;
			});
		});

		// 处理导航点点击
		dots.forEach((dot, index) => {
			dot.addEventListener('click', () => {
				this._currentSlide = index;
				this._scrollToSlide(index);
				this._updatePaginationDots();
			});
		});

		// 每个slide独立的导航箭头
		const slideArrows = this.querySelectorAll('.slide-nav-arrow');
		slideArrows.forEach((arrow) => {
			arrow.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();

				const slideIndex = parseInt(arrow.dataset.slide);
				const direction = arrow.dataset.direction;

				if (direction === 'prev' && slideIndex > 0) {
					this._currentSlide = slideIndex - 1;
				} else if (direction === 'next' && slideIndex < this._data.length - 1) {
					this._currentSlide = slideIndex + 1;
				}

				this._scrollToSlide(this._currentSlide);
				this._updatePaginationDots();
			});
		});

		// 添加触摸滑动支持
		this._addTouchSupport(slider);
	}

	_bindDesktopEvents() {
		this._panels.forEach((panel, index) => {
			panel.addEventListener('mouseenter', () => {
				this._hasUserInteracted = true;
				this._expandPanel(index);
			});
		});
	}

	_addTouchSupport(slider) {
		let startX = 0;
		let startY = 0;
		let isDown = false;
		let isHorizontalScroll = false;

		slider.addEventListener('touchstart', (e) => {
			isDown = true;
			isHorizontalScroll = false;
			startX = e.touches[0].clientX;
			startY = e.touches[0].pageY;
		});

		slider.addEventListener('touchmove', (e) => {
			if (!isDown) return;

			const currentX = e.touches[0].clientX;
			const currentY = e.touches[0].pageY;
			const diffX = Math.abs(currentX - startX);
			const diffY = Math.abs(currentY - startY);

			// 如果水平滑动距离大于垂直滑动距离，则认为是水平滑动
			if (!isHorizontalScroll && diffX > diffY && diffX > 10) {
				isHorizontalScroll = true;
			}

			// 只在水平滑动时阻止默认行为
			if (isHorizontalScroll) {
				e.preventDefault();
			}
		});

		slider.addEventListener('touchend', (e) => {
			if (!isDown) return;
			isDown = false;

			if (!isHorizontalScroll) return;

			const endX = e.changedTouches[0].clientX;
			const diffX = startX - endX;
			const threshold = 50; // 最小滑动距离

			if (Math.abs(diffX) > threshold) {
				if (diffX > 0 && this._currentSlide < this._data.length - 1) {
					// 向左滑动，下一张
					this._currentSlide++;
				} else if (diffX < 0 && this._currentSlide > 0) {
					// 向右滑动，上一张
					this._currentSlide--;
				}
				this._scrollToSlide(this._currentSlide);
				this._updatePaginationDots();
			}
		});
	}

	_scrollToSlide(index) {
		const slider = this.querySelector('#mobilePanelSlider');
		if (!slider) return;

		const containerWidth = slider.offsetWidth;
		const slideWidth = containerWidth * 0.96; // 96% 宽度
		slider.scrollTo({
			left: slideWidth * index,
			behavior: 'smooth'
		});
	}

	_updatePaginationDots() {
		const dots = this.querySelectorAll('.pagination-dot');
		dots.forEach((dot, index) => {
			if (index === this._currentSlide) {
				dot.classList.add('active');
				dot.style.background = 'linear-gradient(45deg, #6366f1, #8b5cf6)';
				dot.classList.remove('bg-gray-600');
			} else {
				dot.classList.remove('active');
				dot.style.background = '';
				dot.classList.add('bg-gray-600');
			}
		});
	}

	_expandPanel(index) {
		if (this._currentExpandedIndex === index) return;

		this._panels.forEach((panel, i) => {
			const description = panel.querySelector('p');
			const title = panel.querySelector('h3');
			const isExpanding = i === index;

			panel.style.flex = isExpanding ? '45' : '10';
			description.classList.toggle('delay-500', isExpanding);
			description.classList.toggle('opacity-0', !isExpanding);
			description.classList.toggle('opacity-100', isExpanding);
			description.classList.toggle('duration-700', isExpanding);
			panel.classList.toggle('opacity-60', !isExpanding);
			panel.classList.toggle('opacity-100', isExpanding);
		});

		this._currentExpandedIndex = index;
	}

	_setDefaultExpanded() {
		const lastIndex = this._panels.length - 1;
		this._expandPanel(lastIndex);
	}
}

// 注册自定义元素
customElements.define('a-code27-panel-x', Code27PanelX);
